import Box from '@mui/material/Box';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import debounce from 'lodash/debounce';
import { mindtimeWazuhActiveResponseApi } from 'src/api/mindtime/wazuhActiveResponseApi';
import { Table } from 'src/common';
import { getSecurityAutomatedResponseDetailHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type {
  WazuhActiveResponseAgentSummaryData,
  WazuhActiveResponseItem,
} from 'src/types/mindtime/wazuhActiveResponse';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import {
  ACTIVE_RESPONSE_SEVERITY_FILTER_OPTIONS,
  ACTIVE_RESPONSE_STATUS_FILTER_OPTIONS,
  ActiveResponseSummaryKpiGrid,
  buildActiveResponseAgentQuery,
  getActiveResponseId,
  getActiveResponseListColumns,
  type ActiveResponseAgentFilters,
} from '../../automated-response/automatedResponseDetailModel';
import { useActiveResponseActionLabelOptions } from '../../automated-response/useActiveResponseActionLabelOptions';
import { getAgentActiveResponsePreview } from '../../preview/automatedResponse';
import { EdrDataCard, EdrDataToolbar, type EdrToolbarFilter } from '../../shared/edrPrimitives';

const PAGE_SIZE = 25;

type EndpointAutomatedResponseTabProps = {
  systemWazuhAgentId: string;
};

const EndpointAutomatedResponseTab: FC<EndpointAutomatedResponseTabProps> = ({
  systemWazuhAgentId,
}) => {
  const navigate = useNavigate();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'automated-response');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveResponseAgentFilters>({});
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState<WazuhActiveResponseAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhActiveResponseItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const { options: actionLabelOptions } = useActiveResponseActionLabelOptions({
    systemWazuhAgentId: agentId || undefined,
    isPreviewMode,
    wait,
  });

  const openRowDetails = useCallback(
    (item: WazuhActiveResponseItem) => {
      const id = getActiveResponseId(item);
      if (!id) return;
      navigate(getSecurityAutomatedResponseDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      setSummary(canSummary ? getAgentActiveResponsePreview(agentId).summary : null);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhActiveResponseApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointAutomatedResponseTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const preview = getAgentActiveResponsePreview(agentId);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhActiveResponseApi
      .getResponses(buildActiveResponseAgentQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        setItems(data?.items ?? []);
        setTotalRecords(data?.totalRecords ?? 0);
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointAutomatedResponseTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    page,
    rowsPerPage,
    search,
    filters.status,
    filters.actionLabel,
    filters.severityBand,
    wait,
    isPreviewMode,
    canList,
  ]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getActiveResponseId(item) || item.trigger || '',
      })),
    [items]
  );

  const setFilter = (key: keyof ActiveResponseAgentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({});
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'actionLabel',
        label: 'Action Type',
        icon: <FlashOnIcon style={{ fontSize: 14 }} />,
        options: actionLabelOptions,
        value: filters.actionLabel ?? '',
        multiple: true,
        onChange: (value) => setFilter('actionLabel', value),
      },
      {
        id: 'status',
        label: 'Status',
        icon: <TrendingUpIcon style={{ fontSize: 14 }} />,
        options: ACTIVE_RESPONSE_STATUS_FILTER_OPTIONS,
        value: filters.status ?? '',
        multiple: true,
        onChange: (value) => setFilter('status', value),
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: ACTIVE_RESPONSE_SEVERITY_FILTER_OPTIONS,
        value: filters.severityBand ?? '',
        multiple: true,
        onChange: (value) => setFilter('severityBand', value),
      },
    ],
    [filters.actionLabel, filters.status, filters.severityBand, actionLabelOptions]
  );

  const columns: Column[] = useMemo(
    () => getActiveResponseListColumns('endpoint', openRowDetails),
    [openRowDetails]
  );

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canSummary ? <ActiveResponseSummaryKpiGrid summary={summary} /> : null}

      {canList ? (
        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                debouncedSetSearch(value);
              }}
              searchPlaceholder="Search responses…"
              filters={toolbarFilters}
              onReset={handleReset}
            />
          )}
        >
          <Table
            columns={columns}
            data={rows as unknown as TableData[]}
            loading={listLoading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onRowClick={(row) => openRowDetails(row as unknown as WazuhActiveResponseItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
            MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
          />
        </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointAutomatedResponseTab;
